import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../lib/api.js';
import { TAB_UI_STYLES } from '../styles/tab-ui.js';
import { serverEvents, debounce } from '../lib/events.js';
import { OutletViewCache } from '../lib/outlet-view-cache.js';
import { isRetailProfile } from '../lib/site-config.js';
import type { ServerEvent } from '../lib/events.js';
import type {
  MallCategory,
  StockDetail,
  StockImageEntry,
  StockListItem,
  StockMeta,
  StockPackRecord,
  StockSavePayload,
} from '../lib/api.js';

type StockTab = 'info' | 'components' | 'inventory' | 'sales' | 'purchases' | 'transactions' | 'images';
type Mode = 'list' | 'detail' | 'create';

interface QohRow {
  packTypeId: string;
  code: string;
  packSize: number;
  current: number;
  actual: string;
  cost: number;
}

interface ComponentInfo {
  code: string;
  name: string;
  packs: StockPackRecord[];
}

/** One ACTIVE mall listing — /staff/mall/listings returns only unexpired
 *  listings, so membership in this list means "published right now". */
interface MallListingRow {
  id?: string;
  stockId: string;
  code: string;
  name: string;
  description?: string;
  typeName?: string;
  price: number;
  qoh?: number;
  qohString?: string;
  listingEnd: number;
  packTypeId?: string;
  categoryIds?: string[];
}

/** Everything kept per outlet so flipping back re-draws the exact screen
 *  (search text, results, open item, active tab, open dialogs). */
interface KeptStockView {
  mode: Mode;
  search: string;
  items: StockListItem[];
  publishedOnly: boolean;
  error: string;
  notice: string;
  detail: StockDetail | null;
  draft: StockSavePayload | null;
  dirty: boolean;
  tab: StockTab;
  meta: StockMeta | null;
  editCell: string | null;
  invRows: Array<Record<string, unknown>> | null;
  salesRows: Array<Record<string, unknown>> | null;
  purchaseRows: Array<Record<string, unknown>> | null;
  txRows: Array<Record<string, unknown>> | null;
  images: StockImageEntry[] | null;
  componentInfo: Record<string, ComponentInfo>;
  staleReloadId: string | null;
  qohOpen: boolean;
  qohRows: QohRow[];
  qohTypeId: string;
  pickOpen: boolean;
  pickSearch: string;
  pickItems: StockListItem[];
}

const TABS: Array<{ id: StockTab; label: string; readOnly?: boolean }> = [
  { id: 'info', label: 'ℹ️ Info' },
  { id: 'components', label: '🧪 Components' },
  { id: 'inventory', label: '📊 Inventory', readOnly: true },
  { id: 'sales', label: '🧾 Sales', readOnly: true },
  { id: 'purchases', label: '🚚 Purchases', readOnly: true },
  { id: 'transactions', label: '🔁 Transactions', readOnly: true },
  { id: 'images', label: '🖼️ Images', readOnly: true },
];

/** Retail-stall deployment: plain stock items, a single packing and none of
 *  the pharmacy-only fields (generic name, poison flags, strength, dispensing
 *  instruction, drug warning). Chosen per host from /site-config.js. */
const RETAIL = isRetailProfile();

/** Tabs offered for a mode — create only shows Info (packs/prices live there now);
 *  retail drops the pharmacy-only Components tab. */
function visibleTabs(mode: Mode): Array<{ id: StockTab; label: string; readOnly?: boolean }> {
  const all = mode === 'detail' ? TABS : TABS.filter((t) => t.id === 'info');
  return RETAIL ? all.filter((t) => t.id !== 'components') : all;
}

/**
 * Stock management page — Admin (ET_Admin) can create/edit/adjust/delete;
 * staff (ET_User) gets a read-only view of the same record.
 *
 * The editor works on a full copy of the server record ("draft") because
 * saving replaces sub-collections (taxes, promotions, components, shelves,
 * pack prices) wholesale on the server.
 */
@customElement('stock-manage')
export class StockManage extends LitElement {
  static styles = [TAB_UI_STYLES, css`
    :host { display: flex; flex-direction: column; height: 100%; min-height: 0; overflow: hidden; }
    .page-scroll { flex: 1; overflow-y: auto; min-height: 0; -webkit-overflow-scrolling: touch; }

    /* Detail tabs + list views: the header row stays fixed — only the data list scrolls. */
    .tab-body { flex: 1; min-height: 0; display: flex; flex-direction: column; }
    .tab-body > .table-wrap { flex: 1; min-height: 0; overflow: auto; }
    .tab-body > .section.fill { flex: 1; min-height: 0; display: flex; flex-direction: column; }
    .tab-body > .section.fill > .table-wrap { flex: 1; min-height: 0; overflow: auto; }
    .tab-body > .table-wrap thead th,
    .tab-body > .section.fill > .table-wrap thead th { position: sticky; top: 0; z-index: 1; background: var(--color-surface); border-bottom: none; box-shadow: inset 0 -1px 0 var(--color-border); }

    .search-row { display: flex; gap: 0.5rem; margin-bottom: 1rem; flex-shrink: 0; }
    .search-row input { flex: 1; }
    .stale { display: flex; align-items: center; gap: 0.5rem; color: var(--color-warning, #b06000); font-size: 0.78rem; margin: 0.5rem 0; }
    .fg { margin-bottom: 0.25rem; }
    .check-row { display: flex; gap: 1rem; align-items: center; margin: 0.4rem 0; font-size: 0.82rem; }
    .check-row label { display: flex; gap: 0.35rem; align-items: center; }
    td.num input { max-width: 90px; text-align: right; }
    .table-wrap { overflow-x: auto; }
    .thumb { width: 40px; height: 40px; object-fit: cover; border-radius: 8px; border: 1px solid var(--color-border); }
    .img-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 0.75rem; }
    .img-card { border: 1px solid var(--color-border); border-radius: 12px; padding: 0.4rem; text-align: center; }
    .img-card img { width: 100%; height: 110px; object-fit: contain; background: #fafafa; }
    .list-tools { display: flex; gap: 0.5rem; margin-bottom: 0.75rem; align-items: center; flex-wrap: wrap; flex-shrink: 0; }
    .row-actions { display: flex; gap: 0.35rem; justify-content: flex-end; flex-wrap: wrap; }
    .mall-badge { background: #e6f4ea; color: #137333; }
    .cat-chips { display: flex; flex-wrap: wrap; gap: 0.2rem; margin-top: 0.25rem; }
    .cat-chip { background: #eef2ff; color: #3730a3; border-radius: 999px; padding: 0.05rem 0.4rem; font-size: 0.66rem; }
    .cat-chips { display: flex; flex-wrap: wrap; gap: 0.25rem; margin-top: 0.25rem; }
    .cat-chip { background: #eef2ff; color: #3730a3; border-radius: 999px; padding: 0.1rem 0.45rem; font-size: 0.68rem; }
    .cat-picker { max-height: 280px; overflow-y: auto; border: 1px solid var(--color-border); border-radius: 10px; padding: 0.5rem; }
    .cat-group { margin-bottom: 0.6rem; }
    .cat-group-title { font-weight: 700; font-size: 0.78rem; margin-bottom: 0.25rem; }
    .cat-options { display: flex; flex-wrap: wrap; gap: 0.25rem 0.75rem; }
    .cat-option { display: flex; align-items: center; gap: 0.3rem; font-size: 0.8rem; }

    /* Phones: the 8-column table becomes one card per item so the row
       actions (Edit / Publish / Unpublish) never sit off-screen. */
    @media (max-width: 768px) {
      .stock-table thead { display: none; }
      .stock-table, .stock-table tbody { display: block; }
      .stock-table tbody tr {
        display: flex; flex-wrap: wrap; align-items: baseline; gap: 0 0.5rem;
        border: 1px solid var(--color-border); border-radius: 14px;
        padding: 0.6rem 0.7rem; margin-bottom: 0.6rem; background: var(--color-surface);
      }
      .stock-table tbody td { display: block; border: none; padding: 0.1rem 0; }
      .stock-table .thumb-cell { display: none; }
      .stock-table .code-cell { order: 1; font-weight: 700; }
      .stock-table .price-cell { order: 2; margin-left: auto; font-weight: 700; }
      .stock-table .name-cell { order: 3; flex: 1 1 100%; font-size: 0.95rem; }
      .stock-table .type-cell { order: 4; font-size: 0.75rem; color: var(--color-text-secondary); }
      .stock-table .qoh-cell { order: 5; margin-left: auto; font-size: 0.75rem; color: var(--color-text-secondary); }
      .stock-table .qoh-cell::before { content: 'QOH '; }
      .stock-table .mall-cell { order: 6; flex: 1 1 100%; margin-top: 0.35rem; }
      .stock-table .actions-cell { order: 7; flex: 1 1 100%; margin-top: 0.45rem; }
      .stock-table .row-actions { justify-content: flex-start; }
      .stock-table .row-actions .btn-sm { min-height: 40px; }
    }
  `];

  @property({ type: String }) storeCode = '';
  @property({ type: String }) sessionKey = '';
  @property({ type: Boolean }) isAdmin = false;

  @state() private _mode: Mode = 'list';
  @state() private _search = '';
  @state() private _items: StockListItem[] = [];
  @state() private _loading = false;
  @state() private _saving = false;
  @state() private _error = '';
  @state() private _notice = '';
  @state() private _detail: StockDetail | null = null;
  @state() private _draft: StockSavePayload | null = null;
  @state() private _dirty = false;
  @state() private _tab: StockTab = 'info';
  @state() private _meta: StockMeta | null = null;
  /** Numeric cell currently focused in the Packs & Prices table — rendered raw while typing. */
  @state() private _editCell: string | null = null;

  // Lazy tab data
  @state() private _invRows: Array<Record<string, unknown>> | null = null;
  @state() private _salesRows: Array<Record<string, unknown>> | null = null;
  @state() private _purchaseRows: Array<Record<string, unknown>> | null = null;
  @state() private _txRows: Array<Record<string, unknown>> | null = null;
  @state() private _images: StockImageEntry[] | null = null;
  @state() private _imgUploading = false;

  // QOH adjust dialog
  @state() private _qohOpen = false;
  @state() private _qohRows: QohRow[] = [];
  @state() private _qohTypeId = '';
  @state() private _qohBusy = false;

  // Component picker
  @state() private _pickOpen = false;
  @state() private _pickSearch = '';
  @state() private _pickItems: StockListItem[] = [];
  @state() private _pickBusy = false;

  // Resolved component display info (code/name/packs) by component id
  @state() private _componentInfo: Record<string, ComponentInfo> = {};

  private _resolveInFlight = new Set<string>();

  // ── Online mall (publish/unpublish lives on this stock list) ──────────
  @state() private _listings: MallListingRow[] = [];
  @state() private _listingsLoading = false;
  @state() private _listingsError = '';
  @state() private _publishedOnly = false;
  @state() private _publishStock: StockListItem | null = null;
  @state() private _publishDays = '30';
  @state() private _publishCustomDays = '';
  @state() private _publishNote = '';
  @state() private _publishBusy = false;
  @state() private _publishError = '';
  @state() private _publishCategoryIds: string[] = [];
  @state() private _categorySearch = '';

  // Gateway-owned mall taxonomy (shared by every outlet on this gateway).
  @state() private _mallCategories: MallCategory[] = [];
  @state() private _categoriesLoading = false;
  @state() private _categoriesError = '';

  // ── Live server events (auto-refresh) ──
  @state() private _staleReloadId: string | null = null;
  private _eventUnsubs: Array<() => void> = [];
  private _reloadDetailSoon = debounce((id: string) => void this._refreshDetailQuiet(id), 700);
  private _reloadListSoon = debounce(() => {
    if (this.isConnected && this._mode === 'list') void this._loadList();
  }, 700);

  /** Kept per-outlet view — flipping to another outlet saves this screen and
   *  re-draws it when the user flips back (see OutletViewCache). */
  private _view = new OutletViewCache<KeptStockView>(this, 'stock-manage', {
    capture: () => this._captureView(),
    restore: (v) => this._restoreView(v),
    reset: () => this._resetView(),
  });

  connectedCallback() {
    super.connectedCallback();
    this._eventUnsubs = [
      serverEvents.on('stock', this._onServerEvent),
      serverEvents.on('purchase', this._onServerEvent),
      serverEvents.on('purchaseHold', this._onServerEvent),
      serverEvents.on('salesHold', this._onServerEvent),
      serverEvents.onLifecycle(this._onServerLifecycle),
    ];
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._reloadDetailSoon.cancel();
    this._reloadListSoon.cancel();
    this._eventUnsubs.forEach((fn) => fn());
    this._eventUnsubs = [];
    // Keep the screen the user is leaving (also covers panel-type switches)
    // before this instance is discarded.
    this._view.dispose();
  }

  /**
   * The shell keeps this element mounted when switching to the same subtree of
   * another outlet and only re-binds `.storeCode` — property bindings never
   * fire attributeChangedCallback, so detect the change here. The outlet left
   * keeps its screen (search/results/open item/tab); a first visit loads fresh.
   */
  protected updated() {
    const result = this._view.switchTo(this.storeCode);
    if (result === 'unchanged') return;
    // Drop refreshes queued for the outlet the user just left.
    this._reloadDetailSoon.cancel();
    this._reloadListSoon.cancel();
    // The publish dialog belongs to the outlet that is being left (the
    // published-only filter itself is kept per outlet like the rest of the view).
    this._listings = [];
    this._listingsError = '';
    this._publishStock = null;
    this._publishError = '';
    this._publishCategoryIds = [];
    this._categorySearch = '';
    void this._loadListings();
    if (result === 'fresh') void this._loadList();
  }

  private _captureView(): KeptStockView {
    return {
      mode: this._mode,
      search: this._search,
      items: this._items,
      publishedOnly: this._publishedOnly,
      error: this._error,
      notice: this._notice,
      detail: this._detail,
      draft: this._draft,
      dirty: this._dirty,
      tab: this._tab,
      meta: this._meta,
      editCell: this._editCell,
      invRows: this._invRows,
      salesRows: this._salesRows,
      purchaseRows: this._purchaseRows,
      txRows: this._txRows,
      images: this._images,
      componentInfo: this._componentInfo,
      staleReloadId: this._staleReloadId,
      qohOpen: this._qohOpen,
      qohRows: this._qohRows,
      qohTypeId: this._qohTypeId,
      pickOpen: this._pickOpen,
      pickSearch: this._pickSearch,
      pickItems: this._pickItems,
    };
  }

  private _restoreView(v: KeptStockView) {
    this._mode = v.mode;
    this._search = v.search;
    this._items = v.items;
    this._publishedOnly = v.publishedOnly ?? false;
    this._error = v.error;
    this._notice = v.notice;
    this._detail = v.detail;
    this._draft = v.draft;
    this._dirty = v.dirty;
    // Kept views from older builds may still say 'packs' (now part of Info).
    const keptTab = v.tab as string;
    this._tab = keptTab === 'packs' || (RETAIL && keptTab === 'components') ? 'info' : v.tab;
    this._meta = v.meta;
    this._editCell = v.editCell;
    this._invRows = v.invRows;
    this._salesRows = v.salesRows;
    this._purchaseRows = v.purchaseRows;
    this._txRows = v.txRows;
    this._images = v.images;
    this._componentInfo = v.componentInfo;
    this._staleReloadId = v.staleReloadId;
    this._qohOpen = v.qohOpen;
    this._qohRows = v.qohRows;
    this._qohTypeId = v.qohTypeId;
    this._pickOpen = v.pickOpen;
    this._pickSearch = v.pickSearch;
    this._pickItems = v.pickItems;
    // No request is in flight for a kept screen — keep spinners at rest.
    this._loading = false;
    this._saving = false;
    this._imgUploading = false;
    this._qohBusy = false;
    this._pickBusy = false;
  }

  private _resetView() {
    this._reset();
    this._publishedOnly = false;
    this._meta = null;
    this._loading = false;
    this._saving = false;
    this._imgUploading = false;
    this._qohOpen = false;
    this._qohRows = [];
    this._qohTypeId = '';
    this._qohBusy = false;
    this._pickOpen = false;
    this._pickSearch = '';
    this._pickItems = [];
    this._pickBusy = false;
    this._publishCategoryIds = [];
    this._categorySearch = '';
  }

  private _reset() {
    this._mode = 'list';
    this._search = '';
    this._items = [];
    this._error = '';
    this._notice = '';
    this._detail = null;
    this._draft = null;
    this._dirty = false;
    this._tab = 'info';
    this._clearTabData();
  }

  private _clearTabData() {
    this._invRows = null;
    this._salesRows = null;
    this._purchaseRows = null;
    this._txRows = null;
    this._images = null;
    this._componentInfo = {};
    this._editCell = null;
  }

  // ── Live server events (auto-refresh) ────────────────────────────────

  /**
   * ppServer data-change event for an outlet we subscribed to. Mirrors the
   * desktop behavior: refresh only when the event's record id matches what
   * this panel shows (open detail), otherwise refresh the list.
   */
  private _onServerEvent = (ev: ServerEvent) => {
    if (ev.store !== this.storeCode) return;
    const id = ev.value;
    const detailMatch = this._mode === 'detail' && !!this._detail && String(this._detail.id) === id;

    if (detailMatch) {
      if (ev.code === 'deleted') {
        this._mode = 'list';
        this._detail = null;
        this._draft = null;
        this._dirty = false;
        this._staleReloadId = null;
        this._notice = 'This item was deleted on the server.';
        this._reloadListSoon();
        return;
      }
      if (this._dirty) {
        // Never clobber unsaved edits — offer a manual reload instead.
        this._staleReloadId = id;
        return;
      }
      this._reloadDetailSoon(id);
      return;
    }

    if (this._mode === 'list') this._reloadListSoon();
  };

  /** Connection (re)established / ppServer reconnected — refresh visible view. */
  private _onServerLifecycle = (ev: { event: string }) => {
    if (ev.event !== 'resync') return;
    if (this._mode === 'detail' && this._detail && !this._dirty) {
      this._reloadDetailSoon(String(this._detail.id));
    } else if (this._mode === 'list') {
      this._reloadListSoon();
    }
  };

  private _reloadStaleRecord() {
    const id = this._staleReloadId;
    this._staleReloadId = null;
    if (id) void this._openDetail(id);
  }

  /** Quiet refresh of the open detail: keeps the active tab, re-fetches its data. */
  private async _refreshDetailQuiet(id: string) {
    if (!this.isConnected || this._dirty || this._saving) return;
    if (this._mode !== 'detail' || String(this._detail?.id ?? '') !== id) return;
    const epoch = this._view.epoch;
    try {
      const res = await api.getStockDetail(this.storeCode, id);
      if (!this._view.isCurrent(epoch)) return;
      if (!res.success || !res.data) return;
      if (this._dirty || this._saving) return; // user started editing while fetching
      const tab = this._tab;
      this._detail = res.data;
      this._draft = this._cloneForEdit(res.data);
      this._clearTabData();
      this._tab = tab;
      if (tab !== 'info') void this._switchTab(tab);
    } catch {
      // Transient — the next event / reconnect resync will retry.
    }
  }

  // ── List ──────────────────────────────────────────────────────────────

  private async _loadList() {
    if (!this.storeCode) return;
    void this._ensureCategories();
    const epoch = this._view.epoch;
    this._loading = true;
    this._error = '';
    try {
      const res = await api.stockManageSearch(this.storeCode, this._search);
      if (!this._view.isCurrent(epoch)) return;
      this._items = res.success ? res.data : [];
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = err instanceof Error ? err.message : 'Failed to load stock';
    } finally {
      if (this._view.isCurrent(epoch)) this._loading = false;
    }
  }

  // ── Online mall publishing ────────────────────────────────────────────

  /** Load the gateway's category tree once (it is shared by all outlets). */
  private async _ensureCategories() {
    if (this._mallCategories.length || this._categoriesLoading) return;
    const epoch = this._view.epoch;
    this._categoriesLoading = true;
    this._categoriesError = '';
    try {
      const res = await api.mallCategories();
      if (!this._view.isCurrent(epoch)) return;
      this._mallCategories = res.data || [];
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._categoriesError = err instanceof Error ? err.message : 'Could not load mall categories';
    } finally {
      if (this._view.isCurrent(epoch)) this._categoriesLoading = false;
    }
  }

  private async _loadListings() {
    if (!this.storeCode) return;
    void this._ensureCategories();
    const epoch = this._view.epoch;
    this._listingsLoading = true;
    this._listingsError = '';
    try {
      const res = await api.mallListings(this.storeCode);
      if (!this._view.isCurrent(epoch)) return;
      this._listings = (res.data || []) as MallListingRow[];
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      // Mall state is secondary — stock management keeps working without it.
      this._listings = [];
      this._listingsError = err instanceof Error ? err.message : 'Could not load mall listings';
    } finally {
      if (this._view.isCurrent(epoch)) this._listingsLoading = false;
    }
  }

  private _listingOf(stockId: string): MallListingRow | undefined {
    return this._listings.find((l) => (l.stockId || l.id) === stockId);
  }

  /** Published-only mode lists the mall directly: the listings endpoint
   *  already returns every active listing (not limited to the 50 stock-search
   *  rows), so nothing published can be missed. */
  private _publishedItems(): StockListItem[] {
    const term = this._search.trim().toLowerCase();
    const known = new Map(this._items.map((it) => [it.id, it]));
    return this._listings
      .filter((l) => !term || (l.code || '').toLowerCase().includes(term) || (l.name || '').toLowerCase().includes(term))
      .map((l) => {
        const it = known.get(l.stockId || l.id || '');
        return {
          id: l.stockId || l.id || '',
          code: l.code,
          name: l.name,
          description: l.description || it?.description || '',
          qoh: l.qoh ?? it?.qoh ?? 0,
          qohString: l.qohString || it?.qohString || '',
          price: l.price ?? it?.price ?? 0,
          typeName: l.typeName || it?.typeName || '',
          imageUrl: it?.imageUrl,
          thumbnailUrl: it?.thumbnailUrl,
        } as StockListItem;
      })
      .filter((it) => !!it.id);
  }

  private _formatListingEnd(row: MallListingRow): string {
    if (!row.listingEnd) return 'Until unpublish';
    return new Date(row.listingEnd * 1000).toLocaleDateString();
  }

  private _togglePublishedOnly() {
    this._publishedOnly = !this._publishedOnly;
    this._error = '';
    if (this._publishedOnly) void this._loadListings();
  }

  private _searchAction() {
    if (this._publishedOnly) {
      void this._loadListings();
      return;
    }
    void this._loadList();
  }

  private _listingCategoryNames(row: MallListingRow): string[] {
    return (row.categoryIds || []).map(
      (id) => this._mallCategories.find((c) => c.id === id)?.name || id,
    );
  }

  private _openPublish(it: StockListItem) {
    void this._ensureCategories();
    const listing = this._listingOf(it.id);
    this._publishStock = it;
    this._publishDays = '30';
    this._publishCustomDays = '';
    if (listing?.listingEnd) {
      const remaining = Math.max(1, Math.ceil((listing.listingEnd * 1000 - Date.now()) / 86400000));
      this._publishDays = 'custom';
      this._publishCustomDays = String(remaining);
    } else if (listing) {
      this._publishDays = 'never';
    }
    this._publishNote = '';
    this._publishError = '';
    this._categorySearch = '';
    // Prefill the existing assignment immediately; unknown ids are filtered
    // out at submit time when the taxonomy has loaded (a stale id from a
    // removed category must not block re-publishing).
    this._publishCategoryIds = [...new Set(listing?.categoryIds || [])];
  }

  private _togglePublishCategory(id: string, checked: boolean) {
    if (checked) {
      this._publishCategoryIds = [...new Set([...this._publishCategoryIds, id])];
    } else {
      this._publishCategoryIds = this._publishCategoryIds.filter((x) => x !== id);
    }
    this._publishError = '';
  }

  private async _submitPublish() {
    const stock = this._publishStock;
    if (!stock) return;
    // When the taxonomy is loaded, ignore ids of categories that no longer
    // exist (e.g. the gateway switched vertical or a category was removed).
    const knownIds = new Set(this._mallCategories.map((c) => c.id));
    const chosen = this._mallCategories.length
      ? this._publishCategoryIds.filter((id) => knownIds.has(id))
      : [...this._publishCategoryIds];
    if (!chosen.length) {
      this._publishError = 'Choose at least one category/subcategory.';
      return;
    }
    const epoch = this._view.epoch;
    this._publishBusy = true;
    this._publishError = '';
    try {
      const noEnd = this._publishDays === 'never';
      const custom = parseInt(this._publishCustomDays, 10);
      const days = noEnd ? 0 : this._publishDays === 'custom' ? (isNaN(custom) || custom <= 0 ? 30 : custom) : parseInt(this._publishDays, 10);
      await api.mallPublish(this.storeCode, {
        stockId: stock.id,
        days,
        noEnd,
        note: this._publishNote || undefined,
        categoryIds: chosen,
      });
      if (!this._view.isCurrent(epoch)) return;
      this._publishStock = null;
      this._notice = `“${stock.name || stock.code}” published to the mall.`;
      await this._loadListings();
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      const msg = err instanceof Error && err.message ? err.message : 'Publish failed';
      this._publishError = msg;
      // A rejected publish must be unmistakable — keep the dialog open AND
      // pop the server's reason (poison item, no selling pack, not allowed…).
      window.alert(`Could not publish "${stock.name || stock.code}":\n\n${msg}`);
    } finally {
      if (this._view.isCurrent(epoch)) this._publishBusy = false;
    }
  }

  private async _unpublishStock(it: StockListItem) {
    if (!this.isAdmin) return;
    if (!window.confirm(`Unpublish "${it.name || it.code}" from the mall?`)) return;
    const epoch = this._view.epoch;
    this._error = '';
    try {
      await api.mallUnpublish(this.storeCode, it.id);
      if (!this._view.isCurrent(epoch)) return;
      this._notice = `“${it.name || it.code}” is no longer published.`;
      await this._loadListings();
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      const msg = err instanceof Error && err.message ? err.message : 'Unpublish failed';
      this._error = msg;
      window.alert(`Could not unpublish "${it.name || it.code}":\n\n${msg}`);
    }
  }

  private async _openDetail(id: string) {
    const epoch = this._view.epoch;
    this._loading = true;
    this._error = '';
    this._notice = '';
    try {
      const res = await api.getStockDetail(this.storeCode, id);
      if (!this._view.isCurrent(epoch)) return;
      if (!res.success || !res.data) throw new Error('Stock item not found');
      this._detail = res.data;
      this._draft = this._cloneForEdit(res.data);
      this._dirty = false;
      this._tab = 'info';
      this._mode = 'detail';
      this._clearTabData();
      // Info now hosts the packs/prices table — its columns come from meta.
      void this._ensureMeta();
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = err instanceof Error ? err.message : 'Failed to load item';
    } finally {
      if (this._view.isCurrent(epoch)) this._loading = false;
    }
  }

  private _cloneForEdit(d: StockDetail): StockSavePayload {
    const clone = JSON.parse(JSON.stringify(d)) as StockSavePayload;
    clone.typeId = d.type?.id || '';
    return clone;
  }

  // ── Create ────────────────────────────────────────────────────────────

  private async _startCreate() {
    const epoch = this._view.epoch;
    this._error = '';
    this._notice = '';
    await this._ensureMeta();
    if (!this._view.isCurrent(epoch)) return;
    const firstType = this._meta?.stockTypes[0]?.id || '';
    this._detail = null;
    this._draft = {
      code: '',
      name: '',
      typeId: firstType,
      type: { id: firstType, name: '' },
      parentId: '',
      regNo: '',
      colour: '',
      detail: '',
      barCode: '',
      brand: '',
      genericName: '',
      strength: '',
      remark: '',
      alternateName: '',
      additionalPacking: '',
      eInvoiceClassification: '',
      displayName: '',
      fcc: '',
      rewardPointFactor: 0,
      reorderLevel: 0,
      reorderQty: 0,
      isPoison: false,
      isPseudoPoison: false,
      dispensingInstruction: '',
      drugWarning: '',
      minProfitMargin: '',
      synonymTo: '',
      weightedAvgCost: 0,
      latestCost: 0,
      maxCost: 0,
      averageCostPpos: 0,
      attributes: [],
      packRecords: [this._newPack()],
      taxAndCharges: [],
      salesPromotionList: [],
      componentList: [],
      shelfList: [],
      salesReps: [],
    };
    this._dirty = false;
    this._tab = 'info';
    this._mode = 'create';
    this._clearTabData();
    if (this._meta?.serverKey.autoGenerateItemCode && firstType) {
      await this._loadNextCode(firstType);
    }
  }

  private _newPack(): StockPackRecord {
    return {
      packType: { id: '', code: 'PCS', name: 'PCS' },
      packDescription: '',
      packSize: 1,
      sellingPrice: 1,
      membershipPrice: 1,
      maxDiscountPercentage: 0,
      percentAboveCost: 0,
      barcode: '',
      priceByCategory: (this._meta?.priceCategories || []).map((c) => ({
        typeId: c.id,
        code: c.code,
        name: c.name,
        percentAboveCost: c.limitValue || '0',
        price: 0,
      })),
    };
  }

  private async _loadNextCode(typeId: string) {
    const epoch = this._view.epoch;
    try {
      const res = await api.getStockNextCode(this.storeCode, typeId);
      if (!this._view.isCurrent(epoch)) return;
      if (res.success && this._draft) this._draft.code = res.data.code;
    } catch {
      /* non-fatal — admin can type the code */
    }
  }

  private _ensureMeta(): Promise<void> {
    if (this._meta) return Promise.resolve();
    const epoch = this._view.epoch;
    return api
      .getStockMeta(this.storeCode)
      .then((res) => {
        if (res.success && this._view.isCurrent(epoch)) this._meta = res.data;
      })
      .catch(() => {
        /* meta is best-effort */
      });
  }

  // ── Save / discard ────────────────────────────────────────────────────

  private async _save() {
    const d = this._draft;
    if (!d) return;
    if (!d.code) {
      this._error = 'Stock code is required';
      return;
    }
    if (!d.name) {
      this._error = 'Stock name is required';
      return;
    }
    if (!d.typeId) {
      this._error = 'Stock type is required';
      return;
    }
    const newPacks = (d.packRecords || []).filter((p) => !p.packType?.id);
    if (RETAIL) {
      // The pack code is not editable in retail — new packs always sell as PCS.
      for (const p of newPacks) {
        if (!p.packType?.code?.trim()) p.packType = { ...p.packType, code: 'PCS', name: 'PCS' };
      }
    }
    for (const p of newPacks) {
      if (!p.packType?.code?.trim()) {
        this._error = 'Each new pack needs a pack code';
        return;
      }
    }

    this._saving = true;
    this._error = '';
    this._notice = '';
    const epoch = this._view.epoch;
    const wasCreate = this._mode === 'create';
    try {
      let res: { success: boolean; data?: StockDetail | null };
      if (wasCreate) {
        res = await api.createStock(this.storeCode, d);
      } else {
        if (!this._detail) throw new Error('No stock item loaded');
        res = await api.updateStock(this.storeCode, this._detail.id, d);
      }
      if (!this._view.isCurrent(epoch)) return;
      if (!res.success || !res.data) throw new Error('Save failed');
      this._detail = res.data;
      this._draft = this._cloneForEdit(res.data);
      this._dirty = false;
      this._mode = 'detail';
      this._notice = wasCreate ? 'Stock item created' : 'Saved';
      this._clearTabData();
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = err instanceof Error ? err.message : 'Save failed';
    } finally {
      if (this._view.isCurrent(epoch)) this._saving = false;
    }
  }

  private _discard() {
    if (!this._detail) return;
    if (this._dirty && !window.confirm('Discard unsaved changes?')) return;
    this._draft = this._cloneForEdit(this._detail);
    this._dirty = false;
    this._error = '';
    this._notice = '';
  }

  private _backToList() {
    if (this._dirty && !window.confirm('Discard unsaved changes?')) return;
    this._reset();
    void this._loadList();
  }

  private async _deleteStock() {
    if (!this._detail) return;
    if (!window.confirm(`Delete stock "${this._detail.name}"? This can be undone from the desktop app.`)) return;
    const epoch = this._view.epoch;
    this._loading = true;
    try {
      await api.deleteStock(this.storeCode, this._detail.id);
      if (!this._view.isCurrent(epoch)) return;
      this._reset();
      await this._loadList();
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = err instanceof Error ? err.message : 'Delete failed';
    } finally {
      if (this._view.isCurrent(epoch)) this._loading = false;
    }
  }

  private async _hideStock() {
    if (!this._detail) return;
    if (!window.confirm(`Hide (deactivate) stock "${this._detail.name}"?`)) return;
    const epoch = this._view.epoch;
    this._loading = true;
    try {
      await api.hideStock(this.storeCode, this._detail.id);
      if (!this._view.isCurrent(epoch)) return;
      this._notice = 'Stock hidden';
      this._reset();
      await this._loadList();
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = err instanceof Error ? err.message : 'Hide failed';
    } finally {
      if (this._view.isCurrent(epoch)) this._loading = false;
    }
  }

  // ── Tabs ──────────────────────────────────────────────────────────────

  private async _switchTab(tab: StockTab) {
    if (RETAIL && tab === 'components') return; // pharmacy-only tab, not offered in retail
    this._tab = tab;
    this._error = '';
    if (tab === 'components') this._resolveComponents();
    const epoch = this._view.epoch;
    const id = this._detail?.id;
    if (!id) return;
    try {
      if (tab === 'inventory' && !this._invRows) {
        const res = await api.getStockInventory(this.storeCode, id);
        if (!this._view.isCurrent(epoch)) return;
        this._invRows = res.success ? res.data : [];
      } else if (tab === 'sales' && !this._salesRows) {
        const res = await api.getStockSalesHistory(this.storeCode, id);
        if (!this._view.isCurrent(epoch)) return;
        this._salesRows = res.success ? res.data : [];
      } else if (tab === 'purchases' && !this._purchaseRows) {
        const res = await api.getStockPurchaseHistory(this.storeCode, id);
        if (!this._view.isCurrent(epoch)) return;
        this._purchaseRows = res.success ? res.data : [];
      } else if (tab === 'transactions' && !this._txRows) {
        const res = await api.getStockTransactions(this.storeCode, id);
        if (!this._view.isCurrent(epoch)) return;
        this._txRows = res.success ? res.data : [];
      } else if (tab === 'images' && !this._images) {
        await this._loadImages();
      }
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = err instanceof Error ? err.message : 'Failed to load data';
    }
  }

  // ── QOH adjust ────────────────────────────────────────────────────────

  private async _openQoh() {
    if (!this._detail || !this._draft) return;
    const epoch = this._view.epoch;
    await this._ensureMeta();
    if (!this._view.isCurrent(epoch) || !this._detail || !this._draft) return;
    const packs = (this._draft.packRecords || [])
      .filter((p) => p.packSize > 0)
      .sort((a, b) => b.packSize - a.packSize);
    if (packs.length === 0) {
      this._error = 'This item has no pack records';
      return;
    }
    const cost = this._detail.weightedAvgCost || 0;
    const rows: QohRow[] = [];
    let qoh = Math.floor(this._detail.qoh);
    const frac = this._detail.qoh - qoh;
    const hasFractional = packs.some((p) => Math.floor(p.packSize) < 1);
    for (const p of packs) {
      const pkSize = Math.floor(p.packSize);
      if (pkSize < 1) continue;
      const cur = Math.floor(qoh / pkSize);
      rows.push({
        packTypeId: p.packType.id,
        code: p.packType.code,
        packSize: p.packSize,
        current: cur,
        actual: String(cur),
        cost: cost * p.packSize,
      });
      qoh = Math.trunc(qoh) % Math.trunc(pkSize);
    }
    if (hasFractional) {
      let fq = frac * 10;
      for (const p of packs) {
        let pk = p.packSize - Math.floor(p.packSize);
        if (pk > 0) {
          pk *= 10;
          const cur = Math.floor(fq / pk);
          rows.push({
            packTypeId: p.packType.id,
            code: p.packType.code,
            packSize: p.packSize,
            current: cur,
            actual: String(cur),
            cost: cost * p.packSize,
          });
          fq = Math.trunc(fq) % Math.trunc(pk);
        }
      }
    }
    this._qohRows = rows;
    this._qohTypeId = this._meta?.adjustTypes[0]?.id || '';
    this._qohOpen = true;
  }

  private _qohSubmit() {
    if (!this._detail || !this._qohRows.length) return;
    if (!this._qohTypeId) {
      this._error = 'Select the type of adjustment';
      return;
    }
    let prefix = '';
    let qohNew = 0;
    let val = 0;
    for (const r of this._qohRows) {
      const s = (r.actual || '').trim();
      if (!prefix) prefix = s.charAt(0);
      const numeric = s.startsWith('=') || s.startsWith('+') || s.startsWith('-') ? s.slice(1) : s;
      const q = parseFloat(numeric) || 0;
      qohNew += q * r.packSize;
      val += q * r.cost;
    }
    if (prefix === '+' || prefix === '-') {
      qohNew += this._detail.qoh;
      val += this._detail.qoh * (this._detail.weightedAvgCost || 0);
    }
    const cost = qohNew === 0 || val === 0 ? this._detail.weightedAvgCost || 0 : val / qohNew;
    const diff = qohNew - this._detail.qoh;
    if (Math.abs(diff) < 1e-9) {
      this._error = 'No quantity change entered';
      return;
    }
    const smallest = [...this._qohRows].sort((a, b) => a.packSize - b.packSize)[0];

    this._qohBusy = true;
    this._error = '';
    const epoch = this._view.epoch;
    api
      .adjustStockQoh(this.storeCode, this._detail.id, {
        quantity: diff,
        adjustTypeId: this._qohTypeId,
        packTypeId: smallest.packTypeId,
        packSize: smallest.packSize,
        unitCost: cost,
        costValue: cost * diff,
      })
      .then(async () => {
        if (!this._view.isCurrent(epoch)) return;
        this._qohOpen = false;
        this._notice = `QOH adjusted by ${diff}`;
        await this._openDetail(this._detail!.id);
      })
      .catch((err) => {
        if (!this._view.isCurrent(epoch)) return;
        this._error = err instanceof Error ? err.message : 'Adjust failed';
      })
      .finally(() => {
        if (this._view.isCurrent(epoch)) this._qohBusy = false;
      });
  }

  // ── Components ────────────────────────────────────────────────────────

  private _resolveComponents() {
    for (const c of this._draft?.componentList || []) {
      void this._resolveComponent(c.componentId);
    }
  }

  private async _resolveComponent(componentId: string) {
    if (!componentId || this._componentInfo[componentId] || this._resolveInFlight.has(componentId)) return;
    this._resolveInFlight.add(componentId);
    const epoch = this._view.epoch;
    try {
      const res = await api.getStockDetail(this.storeCode, componentId);
      if (!this._view.isCurrent(epoch)) return;
      if (res.success && res.data) {
        this._componentInfo = {
          ...this._componentInfo,
          [componentId]: {
            code: res.data.code,
            name: res.data.name,
            packs: res.data.packRecords || [],
          },
        };
      }
    } catch {
      /* display fallback is the raw id */
    } finally {
      this._resolveInFlight.delete(componentId);
    }
  }

  private async _openPicker() {
    this._pickOpen = true;
    this._pickSearch = '';
    this._pickItems = [];
    await this._pickerSearch();
  }

  private async _pickerSearch() {
    const epoch = this._view.epoch;
    this._pickBusy = true;
    try {
      const res = await api.stockManageSearch(this.storeCode, this._pickSearch);
      if (!this._view.isCurrent(epoch)) return;
      this._pickItems = res.success ? res.data : [];
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = err instanceof Error ? err.message : 'Search failed';
    } finally {
      if (this._view.isCurrent(epoch)) this._pickBusy = false;
    }
  }

  private async _pickComponent(item: StockListItem) {
    if (!this._draft) return;
    await this._resolveComponent(item.id);
    const info = this._componentInfo[item.id];
    const packs = (info?.packs || []).filter((p) => p.packSize > 0);
    const smallest = [...packs].sort((a, b) => a.packSize - b.packSize)[0];
    const list = this._draft.componentList || (this._draft.componentList = []);
    list.push({
      componentId: item.id,
      quantity: 1,
      costFactor: 1,
      packTypeId: smallest?.packType.id || '',
    });
    this._dirty = true;
    this._pickOpen = false;
  }

  private _removeComponent(index: number) {
    if (!this._draft?.componentList) return;
    this._draft.componentList.splice(index, 1);
    this._dirty = true;
  }

  // ── Images ────────────────────────────────────────────────────────────

  private async _loadImages() {
    if (!this._detail) return;
    const epoch = this._view.epoch;
    const res = await api.getStockImages(this.storeCode, this._detail.id);
    if (!this._view.isCurrent(epoch)) return;
    this._images = res.success ? res.data : [];
  }

  private _imgUrl(img: StockImageEntry, thumb: boolean): string {
    const sk = this.sessionKey ? `sk=${encodeURIComponent(this.sessionKey)}&` : '';
    const fp = thumb ? img.thumbPath || img.filePath : img.filePath;
    const thumbParam = thumb ? 'thumb=1&' : '';
    return `/api/images/${encodeURIComponent(this.storeCode)}/${encodeURIComponent(img.stockId)}/${img.imageNo}?${sk}${thumbParam}fp=${encodeURIComponent(fp)}`;
  }

  private async _onImageSelected(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !this._detail) return;
    const epoch = this._view.epoch;
    this._imgUploading = true;
    this._error = '';
    try {
      await api.uploadStockImage(this.storeCode, this._detail.id, file);
      if (!this._view.isCurrent(epoch)) return;
      await this._loadImages();
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = err instanceof Error ? err.message : 'Upload failed';
    } finally {
      if (this._view.isCurrent(epoch)) this._imgUploading = false;
    }
  }

  private async _deleteImage(img: StockImageEntry) {
    if (!this._detail) return;
    if (!window.confirm(`Delete image ${img.imageNo + 1}?`)) return;
    const epoch = this._view.epoch;
    this._error = '';
    try {
      await api.deleteStockImage(this.storeCode, this._detail.id, img.imageNo);
      if (!this._view.isCurrent(epoch)) return;
      await this._loadImages();
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = err instanceof Error ? err.message : 'Delete failed';
    }
  }

  // ── Field helpers ─────────────────────────────────────────────────────

  private _setField(key: keyof StockSavePayload | string, value: unknown) {
    if (!this._draft) return;
    (this._draft as Record<string, unknown>)[key] = value;
    this._dirty = true;
  }

  private _text(label: string, key: string, value: string, type = 'text') {
    if (!this.isAdmin) {
      return html`<div class="fg"><label>${label}</label><div class="ro">${value || '—'}</div></div>`;
    }
    return html`
      <div class="fg">
        <label>${label}</label>
        <input type=${type} .value=${value} data-testid=${`stock-field-${key}`}
          @input=${(e: InputEvent) => this._setField(key, (e.target as HTMLInputElement).value)} />
      </div>`;
  }

  private _number(label: string, key: string, value: number) {
    if (!this.isAdmin) {
      return html`<div class="fg"><label>${label}</label><div class="ro">${value}</div></div>`;
    }
    return html`
      <div class="fg">
        <label>${label}</label>
        <input type="number" step="any" .value=${String(value ?? 0)} data-testid=${`stock-field-${key}`}
          @input=${(e: InputEvent) =>
            this._setField(key, parseFloat((e.target as HTMLInputElement).value) || 0)} />
      </div>`;
  }

  // ── Render: list ──────────────────────────────────────────────────────

  private _renderList() {
    const listingMode = this._publishedOnly;
    const rows = listingMode ? this._publishedItems() : this._items;
    const busy = listingMode ? this._listingsLoading : this._loading;
    return html`
      <div class="list-tools">
        <div class="search-row" style="flex:1;margin:0;">
          <input placeholder=${listingMode ? 'Filter published items by name or code…' : 'Search stock by name or code…'}
            data-testid="stock-search" .value=${this._search}
            @input=${(e: InputEvent) => (this._search = (e.target as HTMLInputElement).value)}
            @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this._searchAction()} />
          <button class="btn" data-testid="stock-search-btn" @click=${() => this._searchAction()}>Search</button>
        </div>
        <button class="chip ${listingMode ? 'selected' : ''}" data-testid="stock-published-only"
          @click=${() => this._togglePublishedOnly()}>
          🌐 Published only${this._listings.length ? ` (${this._listings.length})` : ''}
        </button>
        ${this.isAdmin
          ? html`<button class="btn" data-testid="stock-new" @click=${() => this._startCreate()}>+ New Stock</button>`
          : ''}
      </div>

      <div class="tab-body">
        ${busy ? html`<div class="loading">Loading…</div>` : ''}
        ${!busy && listingMode && rows.length === 0
          ? html`<div class="empty">${this._listingsError
              ? this._listingsError
              : this._listings.length === 0
                ? 'Nothing is published to the online mall for this outlet yet.'
                : `No published items match “${this._search.trim()}”.`}</div>`
          : ''}
        ${!busy && !listingMode && rows.length === 0
          ? html`<div class="empty">No stock items found. Type a search term to look up stock.</div>`
          : ''}
        ${!busy && rows.length > 0
          ? html`<p class="hint">
              Click any item to open it${this.isAdmin ? ' — its fields are editable, then press Save' : ''}.
              ${this.isAdmin ? ' Use 🌐 Publish to list an item on the online mall.' : ''}
            </p>`
          : ''}
        ${rows.length > 0
          ? html`
              <div class="table-wrap">
                <table class="stock-table">
                  <thead>
                    <tr><th></th><th>Code</th><th>Name</th><th>Type</th><th class="num">QOH</th><th class="num">Price</th><th>Mall</th><th></th></tr>
                  </thead>
                  <tbody>
                    ${rows.map((it) => {
                      const listing = this._listingOf(it.id);
                      return html`
                        <tr class="clickable" @click=${() => this._openDetail(it.id)}
                          @dblclick=${() => this._openDetail(it.id)}>
                          <td class="thumb-cell">${it.thumbnailUrl
                            ? html`<img class="thumb" src=${it.thumbnailUrl} alt="" />`
                            : ''}</td>
                          <td class="code-cell">${it.code}</td>
                          <td class="name-cell">${it.name}</td>
                          <td class="type-cell">${it.typeName}</td>
                          <td class="num qoh-cell">${it.qohString || this._fmtNum(it.qoh)}</td>
                          <td class="num price-cell">${this._fmtNum(it.price)}</td>
                          <td class="mall-cell">
                            ${listing
                              ? html`<span class="badge mall-badge">🌐 Published</span>
                                  ${this._listingCategoryNames(listing).length
                                    ? html`<div class="cat-chips">
                                        ${this._listingCategoryNames(listing).map(
                                          (name) => html`<span class="cat-chip">${name}</span>`)}
                                      </div>`
                                    : html`<div class="muted" style="font-size:0.7rem;margin-top:2px">⚠️ Uncategorised</div>`}
                                  <div class="muted" style="font-size:0.72rem;margin-top:2px">${this._formatListingEnd(listing)}</div>`
                              : html`<span class="muted">Not published</span>`}
                          </td>
                          <td class="actions-cell">
                            <div class="row-actions">
                              <button class="btn-sm" @click=${(e: Event) => {
                                e.stopPropagation();
                                this._openDetail(it.id);
                              }}>${this.isAdmin ? '✏️ Edit' : '👁 View'}</button>
                              ${this.isAdmin
                                ? listing
                                  ? html`<button class="btn-sm" data-testid="stock-recategorise"
                                      @click=${(e: Event) => {
                                        e.stopPropagation();
                                        this._openPublish(it);
                                      }}>🏷️ Categories</button>
                                    <button class="btn-sm btn-danger" data-testid="stock-unpublish"
                                      @click=${(e: Event) => {
                                        e.stopPropagation();
                                        void this._unpublishStock(it);
                                      }}>Unpublish</button>`
                                  : html`<button class="btn-sm" data-testid="stock-publish"
                                      @click=${(e: Event) => {
                                        e.stopPropagation();
                                        this._openPublish(it);
                                      }}>🌐 Publish…</button>`
                                : ''}
                            </div>
                          </td>
                        </tr>`;
                    })}
                  </tbody>
                </table>
              </div>`
          : ''}
      </div>
    `;
  }

  // ── Render: detail/edit ───────────────────────────────────────────────

  private _renderDetail() {
    const d = this._draft;
    if (!d) return html`<div class="empty">No item loaded.</div>`;
    const tabs = visibleTabs(this._mode);
    return html`
      <div class="detail-head">
        <button class="btn-sm" @click=${() => this._backToList()}>← Back</button>
        <h3>${this._mode === 'create' ? 'New Stock Item' : `${d.code} — ${d.name}`}</h3>
        ${this._mode === 'detail' && d.type?.name ? html`<span class="badge">${d.type.name}</span>` : ''}
        <div class="spacer"></div>
        ${this.isAdmin && this._mode === 'detail'
          ? html`
              <button class="btn-sm" @click=${() => this._openQoh()}>Adjust QOH</button>
              <button class="btn-sm btn-danger" @click=${() => this._hideStock()}>Hide</button>
              <button class="btn-sm btn-danger" @click=${() => this._deleteStock()}>Delete</button>
            `
          : ''}
        ${this.isAdmin
          ? html`
              <button class="btn-sm" ?disabled=${!this._dirty} @click=${() => this._discard()}>Discard</button>
              <button class="btn" data-testid="stock-save" ?disabled=${!this._dirty || this._saving} @click=${() => this._save()}>
                ${this._saving ? 'Saving…' : this._mode === 'create' ? 'Create' : 'Save'}
              </button>
            `
          : ''}
      </div>

      ${this.isAdmin
        ? html`<p class="hint">
            ${this._dirty
              ? '● Unsaved changes — press Save to apply.'
              : 'Change any field below, then press Save (no separate edit mode needed).'}
          </p>`
        : html`<p class="hint">Read-only access — contact an Admin of this store to make changes.</p>`}

      <div class="tabs">
        ${tabs.map(
          (t) => html`
            <button class=${this._tab === t.id ? 'active' : ''} @click=${() => this._switchTab(t.id)}>
              ${t.label}
            </button>`)}
      </div>

      <div class=${this._tab === 'info' || this._tab === 'images' ? 'page-scroll' : 'tab-body'}>
        ${this._tab === 'info' ? this._renderInfoTab() : ''}
        ${this._tab === 'components' ? this._renderComponentsTab() : ''}
        ${this._tab === 'inventory' ? this._renderInventoryTab() : ''}
        ${this._tab === 'sales' ? this._renderSalesTab() : ''}
        ${this._tab === 'purchases' ? this._renderPurchasesTab() : ''}
        ${this._tab === 'transactions' ? this._renderTransactionsTab() : ''}
        ${this._tab === 'images' ? this._renderImagesTab() : ''}
      </div>
    `;
  }

  private _renderInfoTab() {
    const d = this._draft!;
    const meta = this._meta;
    return html`
      <div class="section">
        <h4>Identification</h4>
        <div class="grid">
          <div class="fg">
            <label>Code</label>
            ${this.isAdmin
              ? html`
                  <div style="display:flex;gap:0.35rem;">
                    <input data-testid="stock-field-code" .value=${d.code || ''} @input=${(e: InputEvent) =>
                      this._setField('code', (e.target as HTMLInputElement).value)} />
                    ${meta?.serverKey.autoGenerateItemCode
                      ? html`<button class="btn-sm" title="Generate next code" @click=${async () => {
                          const typeId = d.typeId || d.type?.id || '';
                          if (typeId) await this._loadNextCode(typeId);
                        }}>Gen</button>`
                      : ''}
                  </div>`
              : html`<div class="ro">${d.code || '—'}</div>`}
          </div>
          ${this._text('Name', 'name', d.name || '')}
          ${this._text('Printed Name', 'alternateName', d.alternateName || '')}
          ${this._text('Reg No', 'regNo', d.regNo || '')}
          ${this._text('Barcode', 'barCode', d.barCode || '')}
          ${RETAIL ? '' : this._text('Generic Name', 'genericName', d.genericName || '')}
          ${this._text('Brand', 'brand', d.brand || '')}
          ${RETAIL ? '' : this._text('Strength', 'strength', d.strength || '')}
        </div>
        <div class="grid" style="margin-top:0.5rem;">
          <div class="fg">
            <label>Stock Type</label>
            ${this.isAdmin && meta
              ? html`
                  <select data-testid="stock-field-typeId" .value=${(d.typeId || d.type?.id) ?? ''} @change=${(e: Event) => {
                      const id = (e.target as HTMLSelectElement).value;
                      const t = meta.stockTypes.find((x) => x.id === id);
                      this._setField('typeId', id);
                      this._setField('type', { id, name: t?.name || '' });
                      if (this._mode === 'create' && meta.serverKey.autoGenerateItemCode) {
                        void this._loadNextCode(id);
                      }
                    }}>
                    <option value="">— select —</option>
                    ${meta.stockTypes.map(
                      (t) => html`<option value=${t.id} ?selected=${t.id === (d.typeId || d.type?.id)}>${t.name}</option>`)}
                  </select>`
              : html`<div class="ro">${d.type?.name || '—'}</div>`}
          </div>
          <div class="fg">
            <label>Category</label>
            ${this.isAdmin && meta
              ? html`
                  <select .value=${d.parentId || ''} @change=${(e: Event) =>
                    this._setField('parentId', (e.target as HTMLSelectElement).value)}>
                    <option value="">— uncategorised —</option>
                    ${meta.categories.map(
                      (c) => html`<option value=${c.id} ?selected=${c.id === d.parentId}>${c.description}</option>`)}
                  </select>`
              : html`<div class="ro">${d.parentId || '—'}</div>`}
          </div>
          ${this._number('Reorder Level', 'reorderLevel', d.reorderLevel ?? 0)}
          ${this._number('Reorder Qty', 'reorderQty', d.reorderQty ?? 0)}
        </div>
        <div class="check-row">
          ${RETAIL
            ? ''
            : html`
                <label><input type="checkbox" .checked=${!!d.isPoison} ?disabled=${!this.isAdmin}
                  @change=${(e: Event) => this._setField('isPoison', (e.target as HTMLInputElement).checked)} /> Poison</label>
                <label><input type="checkbox" .checked=${!!d.isPseudoPoison} ?disabled=${!this.isAdmin}
                  @change=${(e: Event) => this._setField('isPseudoPoison', (e.target as HTMLInputElement).checked)} /> Pseudo Poison</label>`}
        </div>
      </div>

      ${this.isAdmin
        ? html`
            <div class="section">
              <h4>Costs</h4>
              <div class="grid">
                ${this._number('Average Cost', 'weightedAvgCost', d.weightedAvgCost ?? 0)}
                ${this._number('Latest Cost', 'latestCost', d.latestCost ?? 0)}
                ${this._number('Highest Cost', 'maxCost', d.maxCost ?? 0)}
                <div class="fg"><label>QOH</label><div class="ro">${d.qoh ?? 0}</div></div>
              </div>
            </div>`
        : ''}

      ${this._renderPacksSection()}

      <div class="section">
        <h4>Notes</h4>
        ${RETAIL
          ? ''
          : html`
              <div class="fg">
                <label>Dispensing Instruction</label>
                ${this.isAdmin
                  ? html`<textarea .value=${d.dispensingInstruction || ''} @input=${(e: InputEvent) =>
                      this._setField('dispensingInstruction', (e.target as HTMLTextAreaElement).value)}></textarea>`
                  : html`<div class="ro">${d.dispensingInstruction || '—'}</div>`}
              </div>
              <div class="fg">
                <label>Drug Warning</label>
                ${this.isAdmin
                  ? html`<textarea .value=${d.drugWarning || ''} @input=${(e: InputEvent) =>
                      this._setField('drugWarning', (e.target as HTMLTextAreaElement).value)}></textarea>`
                  : html`<div class="ro">${d.drugWarning || '—'}</div>`}
              </div>`}
        ${this._text('Remark', 'remark', d.remark || '')}
      </div>

      ${this._renderSalesRepsSection()}
      ${this._renderShelfSection()}
      ${this._renderTaxesSection()}
    `;
  }

  /** Packs & Prices — rendered inline on the Info tab (after Costs). */
  private _renderPacksSection() {
    if (RETAIL) return this._renderSinglePackSection();
    const d = this._draft!;
    const packs = d.packRecords || [];
    const priceCats = this._meta?.priceCategories || [];
    return html`
      <div class="section">
        <h4>Packing & Prices</h4>
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Pack Code</th><th class="num">Size</th><th>Description</th><th class="num">Selling</th><th class="num">Member</th>
                <th class="num">Max Disc%</th><th>Barcode</th>
                ${priceCats.map((c) => html`<th class="num" title="Price by category">${c.name}</th>`)}
                ${this.isAdmin ? html`<th></th>` : ''}
              </tr>
            </thead>
            <tbody>
              ${packs.map((p, i) => {
                const isNew = !p.packType?.id;
                return html`
                  <tr>
                    <td>
                      ${this.isAdmin && isNew
                        ? html`<input .value=${p.packType?.code || ''} @input=${(e: InputEvent) =>
                            this._setPack(i, 'code', (e.target as HTMLInputElement).value)} />`
                        : html`<span>${p.packType?.code || '—'}</span>`}
                    </td>
                    <td class="num">
                      ${this.isAdmin && isNew
                        ? html`<input type="number" step="0.01" .value=${this._packCellValue(i, 'packSize', p.packSize)}
                            @focus=${() => (this._editCell = this._packEditKey(i, 'packSize'))}
                            @blur=${() => (this._editCell = null)}
                            @input=${(e: InputEvent) =>
                            this._setPackNum(i, 'packSize', (e.target as HTMLInputElement).value)} />`
                        : html`<span>${this._fmtNum(p.packSize)}</span>`}
                    </td>
                    <td>
                      ${this.isAdmin
                        ? html`<input .value=${p.packDescription || ''} @input=${(e: InputEvent) =>
                            this._setPack(i, 'packDescription', (e.target as HTMLInputElement).value)} />`
                        : html`<span>${p.packDescription || '—'}</span>`}
                    </td>
                    <td class="num">
                      ${this.isAdmin
                        ? html`<input type="number" step="0.01" .value=${this._packCellValue(i, 'sellingPrice', p.sellingPrice)}
                            @focus=${() => (this._editCell = this._packEditKey(i, 'sellingPrice'))}
                            @blur=${() => (this._editCell = null)}
                            @input=${(e: InputEvent) =>
                            this._setPackNum(i, 'sellingPrice', (e.target as HTMLInputElement).value)} />`
                        : html`<span>${this._fmtNum(p.sellingPrice)}</span>`}
                    </td>
                    <td class="num">
                      ${this.isAdmin
                        ? html`<input type="number" step="0.01" .value=${this._packCellValue(i, 'membershipPrice', p.membershipPrice)}
                            @focus=${() => (this._editCell = this._packEditKey(i, 'membershipPrice'))}
                            @blur=${() => (this._editCell = null)}
                            @input=${(e: InputEvent) =>
                            this._setPackNum(i, 'membershipPrice', (e.target as HTMLInputElement).value)} />`
                        : html`<span>${this._fmtNum(p.membershipPrice)}</span>`}
                    </td>
                    <td class="num">
                      ${this.isAdmin
                        ? html`<input type="number" step="0.01" .value=${this._packCellValue(i, 'maxDiscountPercentage', p.maxDiscountPercentage)}
                            @focus=${() => (this._editCell = this._packEditKey(i, 'maxDiscountPercentage'))}
                            @blur=${() => (this._editCell = null)}
                            @input=${(e: InputEvent) =>
                            this._setPackNum(i, 'maxDiscountPercentage', (e.target as HTMLInputElement).value)} />`
                        : html`<span>${this._fmtNum(p.maxDiscountPercentage)}</span>`}
                    </td>
                    <td>
                      ${this.isAdmin
                        ? html`<input .value=${p.barcode || ''} @input=${(e: InputEvent) =>
                            this._setPack(i, 'barcode', (e.target as HTMLInputElement).value)} />`
                        : html`<span>${p.barcode || '—'}</span>`}
                    </td>
                    ${priceCats.map((c) => {
                      const idx = (p.priceByCategory || []).findIndex((x) => x.typeId === c.id);
                      const pc = idx >= 0 ? p.priceByCategory[idx] : undefined;
                      return html`
                        <td class="num">
                          ${this.isAdmin
                            ? html`<input type="number" step="0.01" .value=${this._packCellValue(i, `cat:${c.id}`, pc?.price ?? 0)}
                            @focus=${() => (this._editCell = this._packEditKey(i, `cat:${c.id}`))}
                            @blur=${() => (this._editCell = null)}
                            @input=${(e: InputEvent) => {
                                const v = parseFloat((e.target as HTMLInputElement).value) || 0;
                                if (!p.priceByCategory) p.priceByCategory = [];
                                if (idx >= 0) p.priceByCategory[idx].price = v;
                                else p.priceByCategory.push({
                                  typeId: c.id, code: c.code, name: c.name,
                                  percentAboveCost: c.limitValue || '0', price: v,
                                });
                                this._dirty = true;
                              }} />`
                            : html`<span>${this._fmtNum(pc?.price ?? 0)}</span>`}
                        </td>`;
                    })}
                    ${this.isAdmin
                      ? html`<td><button class="btn-sm btn-danger" @click=${() => this._removePack(i)}>✕</button></td>`
                      : ''}
                  </tr>`;
              })}
            </tbody>
          </table>
        </div>
        ${this.isAdmin
          ? html`<button class="btn-sm" style="margin-top:0.6rem;" @click=${() => this._addPack()}>+ Add Pack</button>`
          : ''}
        ${packs.some((p) => !p.packType?.id)
          ? html`<p class="err">New pack rows need a pack code (e.g. PCS, BOX, STRIP).</p>`
          : ''}
      </div>
    `;
  }

  /**
   * Retail mode: one packing only — description, selling price, barcode.
   * Legacy multi-pack items keep their other pack records in the draft (they are
   * not rendered and never removed), so saving round-trips them unchanged.
   */
  private _renderSinglePackSection() {
    const packs = this._draft?.packRecords || [];
    const idx = 0;
    const p = packs[idx];
    if (!p) {
      return html`
        <div class="section">
          <h4>Packing & Price</h4>
          <div class="empty">This item has no packing.</div>
        </div>`;
    }
    const extra = Math.max(0, packs.length - 1);
    return html`
      <div class="section">
        <h4>Packing & Price</h4>
        <div class="table-wrap">
          <table>
            <thead>
              <tr><th>Description</th><th class="num">Price</th><th>Barcode</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  ${this.isAdmin
                    ? html`<input placeholder="e.g. 500ml bottle" .value=${p.packDescription || ''}
                        @input=${(e: InputEvent) =>
                          this._setPack(idx, 'packDescription', (e.target as HTMLInputElement).value)} />`
                    : html`<span>${p.packDescription || '—'}</span>`}
                </td>
                <td class="num">
                  ${this.isAdmin
                    ? html`<input type="number" step="0.01" .value=${this._packCellValue(idx, 'sellingPrice', p.sellingPrice)}
                        @focus=${() => (this._editCell = this._packEditKey(idx, 'sellingPrice'))}
                        @blur=${() => (this._editCell = null)}
                        @input=${(e: InputEvent) =>
                          this._setPackNum(idx, 'sellingPrice', (e.target as HTMLInputElement).value)} />`
                    : html`<span>${this._fmtNum(p.sellingPrice)}</span>`}
                </td>
                <td>
                  ${this.isAdmin
                    ? html`<input .value=${p.barcode || ''} @input=${(e: InputEvent) =>
                        this._setPack(idx, 'barcode', (e.target as HTMLInputElement).value)} />`
                    : html`<span>${p.barcode || '—'}</span>`}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="hint">
          Sold as a single pack${p.packType?.code ? ` (${p.packType.code})` : ''} — the item goes on
          sale and to the mall at this price.
        </p>
        ${extra > 0
          ? html`<p class="hint">
              ${extra} further pack${extra === 1 ? '' : 's'} exist on this item and are left unchanged.
            </p>`
          : ''}
      </div>
    `;
  }

  /** Sales representatives linked to this item — read-only, matching the
   *  desktop Info page (the assignment itself is managed elsewhere). */
  private _renderSalesRepsSection() {
    const reps = this._draft?.salesReps || [];
    return html`
      <div class="section">
        <h4>Sales Representatives</h4>
        ${reps.length === 0
          ? html`<div class="empty">No sales representatives assigned.</div>`
          : html`
              <div class="table-wrap">
                <table>
                  <thead>
                    <tr><th>Name</th><th class="num">Price</th></tr>
                  </thead>
                  <tbody>
                    ${reps.map(
                      (r) => html`
                        <tr>
                          <td>${r.name || '—'}</td>
                          <td class="num">${this._fmtNum(r.pricePercent)}</td>
                        </tr>`)}
                  </tbody>
                </table>
              </div>`}
      </div>
    `;
  }

  /** Shelf locations are entity links saved by CODE — the server links an
   *  existing shelf or creates one, so only unsaved (new) rows are editable. */
  private _renderShelfSection() {
    const shelves = this._draft?.shelfList || [];
    return html`
      <div class="section">
        <h4>Shelf</h4>
        ${shelves.length === 0 && !this.isAdmin
          ? html`<div class="empty">No shelf locations linked.</div>`
          : ''}
        ${shelves.length > 0
          ? html`
              <div class="table-wrap">
                <table>
                  <thead>
                    <tr><th>Code</th><th>Name</th>${this.isAdmin ? html`<th></th>` : ''}</tr>
                  </thead>
                  <tbody>
                    ${shelves.map(
                      (s, i) => html`
                        <tr>
                          <td>
                            ${this.isAdmin && !s.id
                              ? html`<input placeholder="e.g. A1" .value=${s.code || ''} @input=${(e: InputEvent) =>
                                  this._setShelf(i, (e.target as HTMLInputElement).value)} />`
                              : html`<span>${s.code || '—'}</span>`}
                          </td>
                          <td>${s.name || '—'}</td>
                          ${this.isAdmin
                            ? html`<td><button class="btn-sm btn-danger" @click=${() => this._removeShelf(i)}>✕</button></td>`
                            : ''}
                        </tr>`)}
                  </tbody>
                </table>
              </div>`
          : ''}
        ${this.isAdmin
          ? html`<button class="btn-sm" style="margin-top:0.6rem;" @click=${() => this._addShelf()}>+ Add Shelf</button>`
          : ''}
        ${shelves.some((s) => !s.id && !(s.code || '').trim())
          ? html`<p class="err">New shelf rows need a shelf code.</p>`
          : ''}
      </div>
    `;
  }

  /** Taxes & charges — matched by CODE on the server (unknown codes are
   *  ignored). Dates are date-only; To is end-of-day, 31 Dec 2999 = Forever. */
  private _renderTaxesSection() {
    const taxes = this._draft?.taxAndCharges || [];
    return html`
      <div class="section">
        <h4>Taxes</h4>
        ${taxes.length === 0 && !this.isAdmin
          ? html`<div class="empty">No taxes or charges on this item.</div>`
          : ''}
        ${taxes.length > 0
          ? html`
              <div class="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Code</th><th class="num">Rate %</th><th>From</th><th>To</th>
                      ${this.isAdmin ? html`<th></th>` : ''}
                    </tr>
                  </thead>
                  <tbody>
                    ${taxes.map(
                      (t, i) => html`
                        <tr>
                          <td>
                            ${this.isAdmin
                              ? html`<input .value=${t.code || ''} @input=${(e: InputEvent) =>
                                  this._setTax(i, (e.target as HTMLInputElement).value)} />`
                              : html`<span>${t.code || '—'}</span>`}
                          </td>
                          <td class="num">
                            ${this.isAdmin
                              ? html`<input type="number" step="0.01" .value=${this._numCellValue(`tax:${i}:percentage`, t.percentage)}
                                  @focus=${() => (this._editCell = `tax:${i}:percentage`)}
                                  @blur=${() => (this._editCell = null)}
                                  @input=${(e: InputEvent) =>
                                  this._setTaxNum(i, (e.target as HTMLInputElement).value)} />`
                              : html`<span>${this._fmtNum(t.percentage)}</span>`}
                          </td>
                          <td>
                            ${this.isAdmin
                              ? html`<input type="date" .value=${this._dateInputValue(t.fromDate)}
                                  @change=${(e: Event) => this._setTaxDate(i, 'fromDate', (e.target as HTMLInputElement).value)} />`
                              : html`<span>${this._fmtDate(t.fromDate)}</span>`}
                          </td>
                          <td>
                            ${this.isAdmin
                              ? html`<input type="date" .value=${this._dateInputValue(t.toDate)}
                                  @change=${(e: Event) => this._setTaxDate(i, 'toDate', (e.target as HTMLInputElement).value)} />`
                              : html`<span>${this._fmtTaxDate(t.toDate)}</span>`}
                          </td>
                          ${this.isAdmin
                            ? html`<td><button class="btn-sm btn-danger" @click=${() => this._removeTax(i)}>✕</button></td>`
                            : ''}
                        </tr>`)}
                  </tbody>
                </table>
              </div>`
          : ''}
        ${this.isAdmin
          ? html`<button class="btn-sm" style="margin-top:0.6rem;" @click=${() => this._addTax()}>+ Add Tax</button>`
          : ''}
        ${taxes.some((t) => !(t.code || '').trim())
          ? html`<p class="err">New tax rows need an existing tax code (e.g. GST).</p>`
          : ''}
      </div>
    `;
  }

  private _setShelf(index: number, code: string) {
    const s = this._draft?.shelfList?.[index];
    if (!s) return;
    s.code = code;
    this._dirty = true;
  }

  private _addShelf() {
    if (!this._draft) return;
    (this._draft.shelfList || (this._draft.shelfList = [])).push({ id: '', code: '', name: '' });
    this._dirty = true;
  }

  private _removeShelf(index: number) {
    const list = this._draft?.shelfList;
    const s = list?.[index];
    if (!list || !s) return;
    if ((s.id || s.code) && !window.confirm(`Remove shelf "${s.code || s.name}"?`)) return;
    list.splice(index, 1);
    this._dirty = true;
  }

  private _setTax(index: number, code: string) {
    const t = this._draft?.taxAndCharges?.[index];
    if (!t) return;
    t.code = code;
    this._dirty = true;
  }

  private _setTaxNum(index: number, raw: string) {
    const t = this._draft?.taxAndCharges?.[index];
    if (!t) return;
    t.percentage = parseFloat(raw) || 0;
    this._dirty = true;
  }

  private _setTaxDate(index: number, key: 'fromDate' | 'toDate', value: string) {
    const t = this._draft?.taxAndCharges?.[index];
    if (!t) return;
    t[key] = this._dateInputToMs(value, key === 'toDate');
    this._dirty = true;
  }

  private _addTax() {
    if (!this._draft) return;
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    (this._draft.taxAndCharges || (this._draft.taxAndCharges = [])).push({
      typeId: '',
      code: 'GST',
      name: '',
      parentId: '',
      percentage: 0,
      fixedValue: 0,
      fromDate: from,
      toDate: new Date(2999, 11, 31, 23, 59, 59).getTime(),
    });
    this._dirty = true;
  }

  private _removeTax(index: number) {
    const list = this._draft?.taxAndCharges;
    const t = list?.[index];
    if (!list || !t) return;
    if ((t.code || '').trim() && !window.confirm(`Remove tax "${t.code}"?`)) return;
    list.splice(index, 1);
    this._dirty = true;
  }

  private _setPack(index: number, key: 'code' | 'packDescription' | 'barcode', value: string) {
    const p = this._draft?.packRecords?.[index];
    if (!p) return;
    if (key === 'code') p.packType = { ...p.packType, code: value, name: value };
    else p[key] = value;
    this._dirty = true;
  }

  private _setPackNum(index: number, key: 'packSize' | 'sellingPrice' | 'membershipPrice' | 'maxDiscountPercentage', raw: string) {
    const p = this._draft?.packRecords?.[index];
    if (!p) return;
    p[key] = parseFloat(raw) || 0;
    // Retail hides the member price — keep it equal to the selling price so a
    // member is never charged a stale/placeholder amount at the POS.
    if (RETAIL && key === 'sellingPrice') p.membershipPrice = p.sellingPrice;
    this._dirty = true;
  }

  /** Identity of a focused numeric cell in the Packs & Prices table. */
  private _packEditKey(row: number, field: string): string {
    return `pack:${row}:${field}`;
  }

  /** Packs & Prices values are all prices/costs — rendered with exactly 2 decimals.
   *  The cell being edited shows its raw value so typing isn't disrupted (formatted on blur). */
  private _packCellValue(row: number, field: string, v: unknown): string {
    return this._numCellValue(this._packEditKey(row, field), v);
  }

  /** Numeric cell bound to `_editCell` — raw while focused, 2 decimals otherwise. */
  private _numCellValue(editKey: string, v: unknown): string {
    if (this._editCell === editKey) {
      const n = typeof v === 'number' ? v : Number(v);
      return Number.isFinite(n) ? String(n) : '0';
    }
    return this._fmtInput(v);
  }

  private _fmtInput(v: unknown): string {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n.toFixed(2) : '0.00';
  }

  private _addPack() {
    if (!this._draft) return;
    (this._draft.packRecords || (this._draft.packRecords = [])).push(this._newPack());
    this._dirty = true;
  }

  private _removePack(index: number) {
    if (!this._draft?.packRecords) return;
    const p = this._draft.packRecords[index];
    if (!p) return;
    if (p.packType?.id && !window.confirm(`Remove pack "${p.packType.code}"? It will no longer be sold.`)) return;
    this._draft.packRecords.splice(index, 1);
    this._dirty = true;
  }

  private _renderComponentsTab() {
    const d = this._draft!;
    const comps = d.componentList || [];
    return html`
      <div class="section fill">
        <h4>Package / Ingredients</h4>
        ${comps.length === 0 ? html`<div class="empty">No components defined.</div>` : ''}
        ${comps.length > 0
          ? html`
              <div class="table-wrap">
                <table>
                  <thead>
                    <tr><th>Component</th><th>Pack</th><th class="num">Quantity</th><th class="num">Cost Factor</th>${this.isAdmin ? html`<th></th>` : ''}</tr>
                  </thead>
                  <tbody>
                    ${comps.map((c, i) => {
                      const info = this._componentInfo[c.componentId];
                      return html`
                        <tr>
                          <td>
                            ${info
                              ? html`<span>[${info.code}] ${info.name}</span>`
                              : html`<span>${c.componentId}</span>`}
                          </td>
                          <td>
                            ${this.isAdmin
                              ? html`
                                  <select .value=${c.packTypeId} @change=${(e: Event) => {
                                    c.packTypeId = (e.target as HTMLSelectElement).value;
                                    this._dirty = true;
                                  }}>
                                    ${(info?.packs || []).map(
                                      (p) => html`<option value=${p.packType.id} ?selected=${p.packType.id === c.packTypeId}>
                                        ${p.packType.code} (${p.packSize})
                                      </option>`)}
                                  </select>`
                              : html`<span>${c.packTypeId || '—'}</span>`}
                          </td>
                          <td class="num">
                            ${this.isAdmin
                              ? html`<input type="number" step="any" .value=${String(c.quantity)} @input=${(e: InputEvent) => {
                                  c.quantity = parseFloat((e.target as HTMLInputElement).value) || 0;
                                  this._dirty = true;
                                }} />`
                              : html`<span>${this._fmtNum(c.quantity)}</span>`}
                          </td>
                          <td class="num">
                            ${this.isAdmin
                              ? html`<input type="number" step="any" .value=${String(c.costFactor)} @input=${(e: InputEvent) => {
                                  c.costFactor = parseFloat((e.target as HTMLInputElement).value) || 0;
                                  this._dirty = true;
                                }} />`
                              : html`<span>${this._fmtNum(c.costFactor)}</span>`}
                          </td>
                          ${this.isAdmin
                            ? html`<td><button class="btn-sm btn-danger" @click=${() => this._removeComponent(i)}>✕</button></td>`
                            : ''}
                        </tr>`;
                    })}
                  </tbody>
                </table>
              </div>`
          : ''}
        ${this.isAdmin
          ? html`<button class="btn-sm" style="margin-top:0.6rem;" @click=${() => this._openPicker()}>+ Add Component</button>`
          : ''}
      </div>
    `;
  }

  private _renderInventoryTab() {
    const rows = this._invRows;
    if (rows === null) return html`<div class="loading">Loading…</div>`;
    if (rows.length === 0) return html`<div class="empty">No inventory movement recorded.</div>`;
    return html`
      <div class="table-wrap">
        <table>
          <thead>
            <tr><th>Month</th><th class="num">Qty In</th><th class="num">Out (Retail)</th><th class="num">Out (Wholesale)</th><th class="num">Adj</th><th class="num">Balance</th></tr>
          </thead>
          <tbody>
            ${rows.map(
              (r) => html`
                <tr>
                  <td>${r['month']}</td>
                  <td class="num">${this._fmtNum(r['quantityIn'])}</td>
                  <td class="num">${this._fmtNum(r['quantityOutRetail'])}</td>
                  <td class="num">${this._fmtNum(r['quantityOutWholesales'])}</td>
                  <td class="num">${this._fmtNum(r['quantityAdj'])}</td>
                  <td class="num">${this._fmtNum(r['runningTotal'])}</td>
                </tr>`)}
          </tbody>
        </table>
      </div>`;
  }

  private _renderSalesTab() {
    const rows = this._salesRows;
    if (rows === null) return html`<div class="loading">Loading…</div>`;
    if (rows.length === 0) return html`<div class="empty">No sales recorded for this item.</div>`;
    return html`
      <div class="table-wrap">
        <table>
          <thead>
            <tr><th>Date</th><th>#</th><th>Customer</th><th class="num">Qty</th><th class="num">Bonus</th><th class="num">Price</th><th class="num">Amount</th><th>Batch</th><th>Staff</th><th>Location</th></tr>
          </thead>
          <tbody>
            ${rows.map(
              (r) => html`
                <tr>
                  <td>${this._fmtDate(r['date'])}</td>
                  <td>${r['refNo']}</td>
                  <td>${r['customer'] || 'WALK-IN'}</td>
                  <td class="num">${this._fmtNum(r['quantity'] ?? 0)}</td>
                  <td class="num">${this._fmtNum(r['bonusQty'] ?? 0)}</td>
                  <td class="num">${this._fmtNum(r['price'])}</td>
                  <td class="num">${this._fmtNum(r['amount'])}</td>
                  <td>${r['batchNo'] || '—'}</td>
                  <td>${r['who'] || '—'}</td>
                  <td>${r['location'] || '—'}</td>
                </tr>`)}
          </tbody>
        </table>
      </div>`;
  }

  private _renderPurchasesTab() {
    const rows = this._purchaseRows;
    if (rows === null) return html`<div class="loading">Loading…</div>`;
    if (rows.length === 0) return html`<div class="empty">No purchases recorded for this item.</div>`;
    return html`
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Date</th><th class="num">#</th><th>RefNo</th><th>Supplier</th>
              <th class="num">Quantity</th><th class="num">Bonus</th>
              <th class="num">Price</th><th class="num">Net Price</th>
              <th>Batch No</th><th class="num">Expiry Date</th>
              <th>Staff</th><th>Time</th><th>Location</th><th class="num">QOH</th>
            </tr>
          </thead>
          <tbody>
            ${rows.map(
              (r) => html`
                <tr>
                  <td>${this._fmtDate(r['date'])}</td>
                  <td class="num">${r['transactionId'] || '—'}</td>
                  <td>${r['refNo'] || '—'}</td>
                  <td>${r['supplier'] || '—'}</td>
                  <td class="num">${this._qtyWithPack(r['quantity'], r['pack'])}</td>
                  <td class="num">${this._qtyWithPack(r['bonusQty'], r['bonusPack'])}</td>
                  <td class="num">${this._costCell(r['price'])}</td>
                  <td class="num">${this._costCell(this._netPrice(r))}</td>
                  <td>${r['batchNo'] || '—'}</td>
                  <td class="num">${r['expiryDate'] ? this._fmtDate(r['expiryDate']) : '—'}</td>
                  <td>${r['who'] || '—'}</td>
                  <td>${this._fmtDateTime(r['dateCreated'])}</td>
                  <td>${r['location'] || '—'}</td>
                  <td class="num">${this._fmtNum(r['qohBalance'])}</td>
                </tr>`)}
          </tbody>
        </table>
      </div>`;
  }

  private _renderTransactionsTab() {
    const rows = this._txRows;
    if (rows === null) return html`<div class="loading">Loading…</div>`;
    if (rows.length === 0) return html`<div class="empty">No stock adjustments recorded.</div>`;
    return html`
      <div class="table-wrap">
        <table>
          <thead>
            <tr><th>Date</th><th>Reason</th><th class="num">Qty</th><th>Pack</th><th class="num">Cost</th><th>Who</th><th>Where</th>${this.isAdmin ? html`<th></th>` : ''}</tr>
          </thead>
          <tbody>
            ${rows.map(
              (r) => html`
                <tr>
                  <td>${this._fmtDate(r['date'])}</td>
                  <td>${r['reason']}</td>
                  <td class="num">${this._fmtNum(r['quantity'] ?? 0)}</td>
                  <td>${r['pack']}</td>
                  <td class="num">${this._fmtNum(r['costValue'])}</td>
                  <td>${r['who'] || '—'}</td>
                  <td>${r['location'] || '—'}</td>
                  ${this.isAdmin
                    ? html`<td><button class="btn-sm btn-danger" @click=${() => this._deleteTransaction(String(r['transactionId']))}>✕</button></td>`
                    : ''}
                </tr>`)}
          </tbody>
        </table>
      </div>`;
  }

  private async _deleteTransaction(transactionId: string) {
    if (!this._detail) return;
    if (!window.confirm('Delete this stock adjustment? The quantity will be recomputed.')) return;
    const epoch = this._view.epoch;
    try {
      await api.deleteStockTransaction(this.storeCode, this._detail.id, transactionId);
      if (!this._view.isCurrent(epoch)) return;
      this._txRows = null;
      await this._switchTab('transactions');
    } catch (err) {
      if (!this._view.isCurrent(epoch)) return;
      this._error = err instanceof Error ? err.message : 'Delete failed';
    }
  }

  private _renderImagesTab() {
    const images = this._images;
    return html`
      <div class="section">
        <h4>Images</h4>
        ${this.isAdmin
          ? html`
              <label class="btn-sm" style="display:inline-block;margin-bottom:0.6rem;cursor:pointer;">
                ${this._imgUploading ? 'Uploading…' : '+ Upload Image'}
                <input type="file" accept="image/*" style="display:none"
                  @change=${(e: Event) => this._onImageSelected(e)} />
              </label>`
          : ''}
        ${images === null ? html`<div class="loading">Loading…</div>` : ''}
        ${images !== null && images.length === 0 ? html`<div class="empty">No images uploaded.</div>` : ''}
        ${images && images.length > 0
          ? html`
              <div class="img-grid">
                ${images.map(
                  (img) => html`
                    <div class="img-card">
                      <img src=${this._imgUrl(img, true)} alt="" loading="lazy" />
                      <div style="font-size:0.7rem;color:var(--color-text-secondary);margin-top:0.3rem;">
                        #${img.imageNo + 1} · ${img.width}×${img.height}
                      </div>
                      ${this.isAdmin
                        ? html`<button class="btn-sm btn-danger" style="margin-top:0.3rem;"
                            @click=${() => this._deleteImage(img)}>Delete</button>`
                        : ''}
                    </div>`)}
              </div>`
          : ''}
      </div>
    `;
  }

  // ── Render: dialogs ───────────────────────────────────────────────────

  private _renderQohDialog() {
    if (!this._qohOpen) return '';
    return html`
      <div class="modal-overlay" @click=${() => (this._qohOpen = false)}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <h4>Adjust Stock — ${this._detail?.code} ${this._detail?.name}</h4>
          <p style="font-size:0.78rem;color:var(--color-text-secondary);margin:0 0 0.6rem;">
            Enter the new actual quantity per pack, or prefix with <b>+</b>/<b>-</b> for a relative change.
            Current QOH: <b>${this._fmtNum(this._detail?.qoh ?? 0)}</b>
          </p>
          <div class="table-wrap">
            <table>
              <thead><tr><th>Packing</th><th class="num">Pack Size</th><th class="num">QOH</th><th class="num">Actual (loose)</th><th class="num">Cost</th></tr></thead>
              <tbody>
                ${this._qohRows.map(
                  (r, i) => html`
                    <tr>
                      <td>${r.code}</td>
                      <td class="num">${this._fmtNum(r.packSize)}</td>
                      <td class="num">${this._fmtNum(r.current)}</td>
                      <td class="num">
                        <input .value=${r.actual} @input=${(e: InputEvent) => {
                          this._qohRows[i].actual = (e.target as HTMLInputElement).value;
                          this._qohRows = [...this._qohRows];
                        }} />
                      </td>
                      <td class="num">${this._fmtNum(r.cost)}</td>
                    </tr>`)}
              </tbody>
            </table>
          </div>
          <div class="fg" style="margin-top:0.6rem;">
            <label>Type of changes</label>
            <select .value=${this._qohTypeId} @change=${(e: Event) =>
              (this._qohTypeId = (e.target as HTMLSelectElement).value)}>
              <option value="">— select —</option>
              ${(this._meta?.adjustTypes || []).map(
                (t) => html`<option value=${t.id} ?selected=${t.id === this._qohTypeId}>${t.name}</option>`)}
            </select>
          </div>
          <div class="btns">
            <button class="btn-sm" @click=${() => (this._qohOpen = false)}>Cancel</button>
            <button class="btn" ?disabled=${this._qohBusy} @click=${() => this._qohSubmit()}>
              ${this._qohBusy ? 'Applying…' : 'Apply Adjustment'}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private _renderPickerDialog() {
    if (!this._pickOpen) return '';
    return html`
      <div class="modal-overlay" @click=${() => (this._pickOpen = false)}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <h4>Select Component Stock</h4>
          <div class="search-row">
            <input placeholder="Search stock…" .value=${this._pickSearch}
              @input=${(e: InputEvent) => (this._pickSearch = (e.target as HTMLInputElement).value)}
              @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this._pickerSearch()} />
            <button class="btn" @click=${() => this._pickerSearch()}>Search</button>
          </div>
          ${this._pickBusy ? html`<div class="loading">Searching…</div>` : ''}
          ${this._pickItems.map(
            (it) => html`
              <div style="padding:0.45rem 0.2rem;border-bottom:1px solid var(--color-border);cursor:pointer;"
                @click=${() => this._pickComponent(it)}>
                <b>${it.code}</b> — ${it.name}
                <span style="color:var(--color-text-secondary);font-size:0.72rem;"> (${it.qohString || it.qoh})</span>
              </div>`)}
          <div class="btns">
            <button class="btn-sm" @click=${() => (this._pickOpen = false)}>Cancel</button>
          </div>
        </div>
      </div>
    `;
  }

  // ── Render: mall dialogs ──────────────────────────────────────────────

  private _renderCategoryPicker() {
    const term = this._categorySearch.trim().toLowerCase();
    const roots = this._mallCategories.filter((c) => !c.parentId && !c.system);
    const children = this._mallCategories.filter((c) => !!c.parentId && !c.system);
    const groups = roots
      .map((root) => {
        const rootMatches = !term || root.name.toLowerCase().includes(term);
        return {
          root,
          children: children.filter(
            (c) => c.parentId === root.id && (rootMatches || c.name.toLowerCase().includes(term)),
          ),
        };
      })
      .filter((g) => g.children.length);
    const selected = new Set(this._publishCategoryIds);

    if (this._categoriesLoading) return html`<div class="loading">Loading categories…</div>`;
    if (this._categoriesError) return html`<div class="error">${this._categoriesError}</div>`;
    if (!roots.length) return html`<div class="error">No categories are configured on this gateway.</div>`;

    return html`
      <div class="form-group">
        <label>Categories &amp; subcategories (choose one or more)</label>
        <input placeholder="Filter subcategories…" .value=${this._categorySearch}
          @input=${(e: InputEvent) => { this._categorySearch = (e.target as HTMLInputElement).value; }} />
        <div class="cat-picker">
          ${groups.map((g) => html`
            <div class="cat-group">
              <div class="cat-group-title">${g.root.name}</div>
              <div class="cat-options">
                ${g.children.map((c) => html`
                  <label class="cat-option">
                    <input type="checkbox" .checked=${selected.has(c.id)}
                      @change=${(e: Event) =>
                        this._togglePublishCategory(c.id, (e.target as HTMLInputElement).checked)} />
                    <span>${c.name}</span>
                  </label>`)}
              </div>
            </div>`)}
          ${groups.length === 0
            ? html`<div class="muted">No subcategory matches “${this._categorySearch.trim()}”.</div>`
            : ''}
        </div>
        <div class="muted" style="margin-top:0.3rem">
          ${this._publishCategoryIds.length} selected — a product can appear in more than one subcategory.
        </div>
      </div>`;
  }

  private _renderPublishDialog() {
    const stock = this._publishStock;
    if (!stock) return '';
    return html`
      <div class="modal-overlay" @click=${(e: Event) => {
        if (e.target === e.currentTarget && !this._publishBusy) this._publishStock = null;
      }}>
        <div class="modal" @click=${(e: Event) => e.stopPropagation()}>
          <h3>Publish "${stock.name || stock.code}" to the mall</h3>
          ${this._publishError ? html`<div class="error">${this._publishError}</div>` : ''}
          <div class="form-group">
            <label>Duration</label>
            <select .value=${this._publishDays} @change=${(e: any) => { this._publishDays = e.target.value; }}>
              <option value="7">7 days</option>
              <option value="14">14 days</option>
              <option value="30">30 days</option>
              <option value="90">90 days</option>
              <option value="custom">Custom…</option>
              <option value="never">Until I unpublish</option>
            </select>
          </div>
          ${this._publishDays === 'custom' ? html`
            <div class="form-group">
              <label>Number of days</label>
              <input type="number" min="1" .value=${this._publishCustomDays}
                @input=${(e: any) => { this._publishCustomDays = e.target.value; }}>
            </div>` : ''}
          ${this._renderCategoryPicker()}
          <div class="form-group">
            <label>Note (optional)</label>
            <input .value=${this._publishNote} @input=${(e: any) => { this._publishNote = e.target.value; }}>
          </div>
          <div class="muted">${RETAIL
            ? 'The item is offered at its selling price. Choose at least one subcategory.'
            : 'The item is offered at its default pack selling price. Poison / pseudo-poison items cannot be published. Choose at least one subcategory.'}</div>
          <div class="modal-footer">
            <button class="btn" ?disabled=${this._publishBusy} @click=${() => { this._publishStock = null; }}>Cancel</button>
            <button class="btn" ?disabled=${this._publishBusy || this._publishCategoryIds.length === 0 || this._categoriesLoading || !!this._categoriesError || !this._mallCategories.length}
              @click=${() => this._submitPublish()}>
              ${this._publishBusy ? 'Publishing…' : 'Publish'}
            </button>
          </div>
        </div>
      </div>`;
  }

  // ── Render ────────────────────────────────────────────────────────────

  render() {
    if (!this.storeCode) return html`<p>Select a store first.</p>`;
    return html`
      ${this._error ? html`<p class="err">${this._error}</p>` : ''}
      ${this._notice ? html`<p class="ok">${this._notice}</p>` : ''}
      ${this._staleReloadId
        ? html`<p class="stale">
            This item was updated on the server — your unsaved edits may be out of date.
            <button class="btn-sm" @click=${this._reloadStaleRecord}>Reload</button>
          </p>`
        : ''}
      ${this._mode === 'list' ? this._renderList() : this._renderDetail()}
      ${this._renderQohDialog()}
      ${this._renderPickerDialog()}
      ${this._renderPublishDialog()}
    `;
  }

  private _fmtDate(ms: unknown): string {
    const n = typeof ms === 'number' ? ms : Number(ms);
    if (!n || Number.isNaN(n)) return '—';
    return new Date(n).toLocaleDateString();
  }

  /** Tax To-dates cap at 31 Dec 2999, which the desktop renders as Forever. */
  private _fmtTaxDate(ms: unknown): string {
    const n = typeof ms === 'number' ? ms : Number(ms);
    if (!n || !Number.isFinite(n)) return '—';
    if (new Date(n).getFullYear() >= 2999) return 'Forever';
    return this._fmtDate(n);
  }

  /** Epoch ms → yyyy-mm-dd for <input type="date"> (date-only on the server). */
  private _dateInputValue(ms: unknown): string {
    const n = typeof ms === 'number' ? ms : Number(ms);
    if (!n || !Number.isFinite(n)) return '';
    const d = new Date(n);
    if (Number.isNaN(d.getTime())) return '';
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  }

  /** <input type="date"> → epoch ms; To-dates go to end of day (desktop parity). */
  private _dateInputToMs(value: string, endOfDay: boolean): number {
    if (!value) return 0;
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return 0;
    return endOfDay
      ? new Date(y, m - 1, d, 23, 59, 59).getTime()
      : new Date(y, m - 1, d).getTime();
  }

  private _fmtDateTime(ms: unknown): string {
    const n = typeof ms === 'number' ? ms : Number(ms);
    if (!n || Number.isNaN(n)) return '—';
    return new Date(n).toLocaleString();
  }

  /** Quantity to 2 decimals with its pack code appended, e.g. "10.00 BOX"
   *  (desktop: FormatSpecial(qty, 2) + pack). */
  private _qtyWithPack(qty: unknown, pack: unknown): string {
    const n = typeof qty === 'number' ? qty : Number(qty);
    const text = Number.isFinite(n) ? this._fmtNum(n) : '—';
    const p = pack == null || pack === '' ? '' : String(pack);
    return p ? `${text} ${p}` : text;
  }

  /** Purchase Price / Net Price are cost values — masked for non-admins
   *  (desktop: CanViewCost check renders "**.**"). */
  private _costCell(v: unknown): string {
    if (!this.isAdmin) return '**.**';
    return this._fmtNum(v);
  }

  /** Net price per pack — mirrors PPOS::GetNetPrice(HistoricalPurchaseRecord,
   *  StockRecord) using the loaded stock pack sizes. */
  private _netPrice(r: Record<string, unknown>): number {
    const packs = this._detail?.packRecords || [];
    const sizeOf = (code: string) => packs.find((p) => p.packType?.code === code)?.packSize || 0;
    const num = (v: unknown) => (typeof v === 'number' ? v : Number(v)) || 0;
    const pksize = sizeOf(String(r['pack'] ?? ''));
    const qty = num(r['quantity']) * pksize + num(r['bonusQty']) * sizeOf(String(r['bonusPack'] ?? ''));
    const value = num(r['amount']);
    if (qty === 0) return 0;
    return (value / qty) * pksize;
  }

  private _fmtNum(v: unknown): string {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n)
      ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : '—';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'stock-manage': StockManage;
  }
}
